import { useEffect } from 'react';

import {
  BrandsContext,
  useGetBrandsOrderQuery,
  useResetBrandOrdersMutation,
  useGetRegionalBrandsOrderQuery,
  useUpdateBrandSelectionMutation,
  useResetRegionalBrandOrdersMutation,
  useUpdateRegionalBrandSelectionMutation
} from '../services';

import { useSnackbar } from './useSnackbar';
import { BrandSelectionUpdate } from '@/types/services';

/**
 * Hook for managing regional-level pricing state and operations
 * Handles loading regional pricing data, managing brands, and exporting regional prices
 */
export const useBrandsOrder = (open: boolean, scope: 'general' | 'regional', onClose: () => void) => {
  const { setSnackbarMessage } = useSnackbar();

  const { data: brandsOrder = [], isError } = useGetBrandsOrderQuery(undefined, { skip: !open || scope !== 'general' });
  const { data: regionalBrandsOrder = [], isError: isRegionalError } = useGetRegionalBrandsOrderQuery(undefined, {
    skip: !open || scope !== 'regional'
  });

  const [updateBrandSelection, { isLoading: updateLoading }] = useUpdateBrandSelectionMutation();
  const [resetBrandOrders, { isLoading: resetLoading }] = useResetBrandOrdersMutation();
  const [updateRegionalBrandSelection, { isLoading: updateRegionalLoading }] =
    useUpdateRegionalBrandSelectionMutation();
  const [resetRegionalBrandOrders, { isLoading: resetRegionalLoading }] = useResetRegionalBrandOrdersMutation();

  const loading = updateLoading || resetLoading;
  const regionalLoading = updateRegionalLoading || resetRegionalLoading;

  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження порядку брендів', 'error');
    }
  }, [isError, setSnackbarMessage]);

  useEffect(() => {
    if (isRegionalError) {
      setSnackbarMessage('Помилка завантаження регіонального порядку брендів', 'error');
    }
  }, [isRegionalError, setSnackbarMessage]);

  const updateBrandsOrder = async (brandUpdates: BrandSelectionUpdate[], context: BrandsContext) => {
    try {
      await updateBrandSelection({ brandUpdates, context }).unwrap();
      setSnackbarMessage('Налаштування брендів успішно оновлено', 'success');

      onClose();
    } catch (err: any) {
      setSnackbarMessage(
        err?.data?.message || err?.response?.data?.message || 'Помилка збереження налаштувань брендів',
        'error'
      );
    }
  };

  const resetOrder = async (context: BrandsContext) => {
    try {
      await resetBrandOrders(context).unwrap();
      setSnackbarMessage('Налаштування брендів скинуто до стандартних', 'success');

      onClose();
    } catch (error: any) {
      setSnackbarMessage('Помилка збереження налаштувань брендів', 'error');
    }
  };

  const updateRegionalBrandsOrder = async (brandUpdates: BrandSelectionUpdate[], context: BrandsContext) => {
    try {
      await updateRegionalBrandSelection({ brandUpdates, context }).unwrap();
      setSnackbarMessage('Регіональні налаштування брендів успішно оновлено', 'success');

      onClose();
    } catch (err: any) {
      setSnackbarMessage(
        err?.data?.message || err?.response?.data?.message || 'Помилка збереження регіональних налаштувань брендів',
        'error'
      );
    }
  };

  const resetRegionalOrder = async (context: BrandsContext) => {
    try {
      await resetRegionalBrandOrders(context).unwrap();
      setSnackbarMessage('Регіональні налаштування брендів скинуто до стандартних', 'success');

      onClose();
    } catch (error: any) {
      setSnackbarMessage('Помилка скидання регіональних налаштувань', 'error');
    }
  };

  return {
    brandsOrder,
    loading,
    regionalLoading,
    regionalBrandsOrder,
    updateBrandsOrder,
    resetOrder,
    updateRegionalBrandsOrder,
    resetRegionalOrder
  };
};
